import type { SubscriptionPlan } from '@planner/types'
import { getPlanEventLimit, getPlanFeatures as getPlanFeaturesFromConfig } from '@/features/subscriptions/planConfig'

/**
 * Returns the event limit for a given subscription plan
 */
export function getSubscriptionLimit(planType: SubscriptionPlan): number {
  return getPlanEventLimit(planType)
}

/**
 * Calculates the end date of a billing period (rolling 30 days from start)
 */
export function calculateBillingPeriodEnd(startDate: Date): Date {
  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + 30)
  return endDate
}

/**
 * Checks if an event date falls within a billing period
 */
export function isEventInBillingPeriod(
  eventDate: Date,
  periodStart: Date
): boolean {
  const periodEnd = calculateBillingPeriodEnd(periodStart)
  return eventDate >= periodStart && eventDate < periodEnd
}

/**
 * Formats subscription status for display
 */
export function formatSubscriptionStatus(status: string): string {
  switch (status) {
    case 'active':
      return 'Aktywna'
    case 'expired':
      return 'Wygasła'
    case 'cancelled':
      return 'Anulowana'
    default:
      return status
  }
}

/**
 * Formats plan name for display
 */
export function formatPlanName(planType: SubscriptionPlan): string {
  switch (planType) {
    case 'free':
      return 'Free'
    case 'starter':
      return 'Starter'
    case 'professional':
      return 'Professional'
    case 'enterprise':
      return 'Enterprise'
    default:
      return planType
  }
}

/**
 * Checks if Google Calendar integration is available for plan
 */
export function hasGoogleCalendarAccess(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).googleCalendar
}

/**
 * Checks if logo on documents is available for plan
 */
export function hasLogoOnDocuments(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).logoOnDocuments
}

/**
 * Checks if basic reports are available for plan
 */
export function hasBasicReports(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).basicReports
}

/**
 * Checks if advanced reports are available for plan
 */
export function hasAdvancedReports(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).advancedReports
}

/**
 * Checks if push notifications are available for plan
 */
export function hasPushNotifications(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).pushNotifications
}

/**
 * Checks if Excel/PDF export is available for plan
 */
export function hasExcelPdfExport(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).excelPdfExport
}

/**
 * Checks if multiple users feature is available for plan
 */
export function hasMultipleUsers(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).multipleUsers
}

/**
 * Checks if multi-venue management is available for plan
 */
export function hasMultiVenueManagement(planType: SubscriptionPlan): boolean {
  return getPlanFeaturesFromConfig(planType).multiVenueManagement
}

/**
 * Gets all available features for a plan
 */
export function getPlanFeatures(planType: SubscriptionPlan): {
  menuConfiguration: boolean
  clientManagement: boolean
  employeeManagement: boolean
  availabilityCalendar: boolean
  reservations: boolean
  analyticsDashboard: boolean
  googleCalendar: boolean
  logoOnDocuments: boolean
  basicReports: boolean
  advancedReports: boolean
  pushNotifications: boolean
  excelPdfExport: boolean
  multipleUsers: boolean
  multiVenueManagement: boolean
} {
  // Keep the public shape stable, but derive from planConfig (pricing source of truth)
  return getPlanFeaturesFromConfig(planType)
}
